import React, { useState, useRef } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { ALLOWED_MIME_TYPES } from '../utils/constants';
import { validateName, validateEmail } from '../utils/formValidation';

/**
 * Get user-friendly list of supported file types
 */
function getSupportedFileTypes(t) {
  const types = new Set();
  
  ALLOWED_MIME_TYPES.forEach(mimeType => {
    if (mimeType.startsWith('video/')) {
      types.add(t('videoFiles'));
    } else if (mimeType.startsWith('audio/')) {
      types.add(t('audioFiles'));
    } else if (mimeType.startsWith('image/')) {
      types.add(t('imageFiles'));
    } else if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('word') || mimeType.includes('excel') || mimeType.includes('powerpoint') || mimeType.includes('text/')) {
      types.add(t('documents'));
    } else if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar') || mimeType.includes('gzip') || mimeType.includes('7z')) {
      types.add(t('archiveFiles'));
    } else if (mimeType === 'application/octet-stream' || mimeType === 'application/x-binary' || mimeType === 'application/x-unknown') {
      types.add(t('rawOtherFormats'));
    }
  });
  
  return Array.from(types).join(', ');
}

/**
 * FileInput component
 * Handles file selection input with guidelines
 */
function FileInput({ fileInputRef, handleFileSelect, loading, selectedProject, areRequiredFieldsValid, userName, userEmail, consentGiven, preSelectedProject }) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);
  const supportedTypes = getSupportedFileTypes(t);
  const isDisabled = loading || !areRequiredFieldsValid;

  // Determine which fields are missing or invalid
  const getMissingFieldsMessage = () => {
    const nameValidation = validateName(userName);
    const emailValidation = validateEmail(userEmail);
    
    const missing = [];
    
    // Project is required unless pre-selected from URL
    if (!selectedProject && !preSelectedProject) {
      missing.push('project');
    }
    if (!nameValidation.valid) {
      missing.push('name');
    }
    if (!emailValidation.valid) {
      missing.push('email');
    }
    if (!consentGiven) {
      missing.push('consent');
    }
    
    if (missing.length === 0) return null;
    
    if (missing.length === 1) {
      if (missing[0] === 'consent') {
        return t('acceptConsent');
      } else if (missing[0] === 'project') {
        return t('selectProjectRequired');
      } else if (missing[0] === 'name') {
        return t('fillName');
      } else if (missing[0] === 'email') {
        return t('fillEmail');
      }
    } else if (missing.length === 2) {
      if (missing.includes('consent') && missing.includes('project')) {
        return t('selectProjectAndConsent');
      } else if (missing.includes('consent') && missing.includes('name')) {
        return t('fillAndConsent', { field: t('yourName').toLowerCase() });
      } else if (missing.includes('consent') && missing.includes('email')) {
        return t('fillAndConsent', { field: t('yourEmail').toLowerCase() });
      } else if (missing.includes('project') && missing.includes('name')) {
        return t('selectProjectAndFill', { field: t('yourName').toLowerCase() });
      } else if (missing.includes('project') && missing.includes('email')) {
        return t('selectProjectAndFill', { field: t('yourEmail').toLowerCase() });
      } else if (missing.includes('name') && missing.includes('email')) {
        return t('fillNameAndEmail');
      }
    } else if (missing.length === 3) {
      if (missing.includes('project') && missing.includes('consent') && missing.includes('name')) {
        return t('selectProjectAndFill', { field: t('yourName').toLowerCase() }) + ' ' + t('acceptConsent');
      } else if (missing.includes('project') && missing.includes('consent') && missing.includes('email')) {
        return t('selectProjectAndFill', { field: t('yourEmail').toLowerCase() }) + ' ' + t('acceptConsent');
      } else if (missing.includes('project') && missing.includes('name') && missing.includes('email')) {
        return t('selectProjectAndFill', { field: t('yourName').toLowerCase() + ' ' + t('yourEmail').toLowerCase() });
      } else if (missing.includes('consent') && missing.includes('name') && missing.includes('email')) {
        return t('fillNameAndEmail') + ' ' + t('acceptConsent');
      }
    } else {
      return t('allRequired');
    }
  };

  const hintMessage = getMissingFieldsMessage();

  // Handle drag and drop - often preserves non-Latin filenames better
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isDisabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    
    if (droppedFiles.length > 0) {
      // Create a synthetic event similar to file input
      const syntheticEvent = {
        target: {
          files: droppedFiles,
          value: '', // Drag-drop doesn't provide path
        },
      };
      
      handleFileSelect(syntheticEvent);
    }
  };

  return (
    <div className="file-upload-section">
      <div className="upload-guidelines">
        <h3>📁 {t('chooseFiles')}</h3>
        <p className="guidelines-intro">{t('selectFilesToShare')}</p>
        
        <div className="guidelines-box">
          <h4>📋 {t('uploadGuidelines')}</h4>
          <ul className="guidelines-list">
            <li>{t('maxFileSize')}</li>
            <li>{t('supportedFormats')} {supportedTypes}</li>
            <li>{t('secureStorage')}</li>
            <li>{t('stayOnPage')}</li>
          </ul>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="file-input">{t('selectFiles')}:</label>
        
        {/* Drag and Drop Zone */}
        <div
          ref={dropZoneRef}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${isDragging ? '#4299e1' : '#cbd5e0'}`,
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: isDragging ? '#ebf8ff' : '#f7fafc',
            transition: 'all 0.2s ease',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            marginBottom: '12px',
          }}
          onClick={() => {
            if (!isDisabled && fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
        >
          {isDragging ? (
            <div style={{ color: '#4299e1', fontWeight: '600' }}>
              📥 {t('dropFilesHere')}
            </div>
          ) : (
            <div style={{ color: '#718096' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📁</div>
              <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                {t('dragDropFiles')}
              </div>
              <div style={{ fontSize: '14px', color: '#a0aec0' }}>
                {t('orClickToSelect')}
              </div>
            </div>
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          id="file-input"
          type="file"
          multiple
          onChange={handleFileSelect}
          disabled={isDisabled}
          style={{ display: 'none' }}
        />
        
        {!areRequiredFieldsValid && hintMessage && (
          <p className="file-input-hint">
            {hintMessage}
          </p>
        )}
      </div>
    </div>
  );
}

export default FileInput;

